import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "src/hooks/use-auth";
import { getGlucoseData } from "src/pages/dashboard/helpers/helpers";
import { GlucObject } from "src/pages/dashboard/helpers/typehelpers";

interface GlucoseContextType {
  allGlucoseData: GlucObject[];
  isLoading: boolean;
  error: string | null;
}

const GlucoseContext = createContext<GlucoseContextType | undefined>(undefined);

interface GlucoseProviderProps {
  children: ReactNode;
}

export const GlucoseProvider: React.FC<GlucoseProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [allGlucoseData, setAllGlucoseData] = useState<GlucObject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGlucoseData = useCallback(async (): Promise<GlucObject[]> => {
    setIsLoading(true);
    setError(null);

    if (!user?.id) {
      setIsLoading(false);
      return [];
    }

    try {
      // Set period from 1-1-2025 to now
      const startDate = new Date("2025-01-01");
      const endDate = new Date();

      const data = await getGlucoseData(user, {
        start: startDate,
        end: endDate,
      });

      return data;
    } catch (err) {
      console.error("Error fetching glucose data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch glucose data"
      );
      setIsLoading(false);
      return [];
    }
  }, [user?.id]);

  useEffect(() => {
    fetchGlucoseData().then((data) => {
      setAllGlucoseData(data);
      setIsLoading(false);
    });
  }, [fetchGlucoseData]);

  const contextValue: GlucoseContextType = {
    allGlucoseData,
    isLoading,
    error,
  };

  return (
    <GlucoseContext.Provider value={contextValue}>
      {children}
    </GlucoseContext.Provider>
  );
};

export const useAllGlucoseData = (): GlucoseContextType => {
  const context = useContext(GlucoseContext);
  if (context === undefined) {
    throw new Error("useAllGlucoseData must be used within a GlucoseProvider");
  }
  return context;
};
